import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import {
  mixins,
  colors,
  animations,
} from 'ech-components/src/core/styles/theme/default';
import { Container } from 'ech-components/src/helpers';
import { useNavigationQuery } from '~/queries/useNavigationQuery';
import { Navbar, Navigation } from '../';
import { LogoHeader } from '../Logo';
import { createRoutingURI } from '~/scripts/utils/domain';

const StyledHeader = styled.header(
  ({ active, scrolled }: { active: boolean; scrolled: boolean }) => css`
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 99;
    transition: box-shadow ${animations.fast},
      background-color ${animations.fast};

    ${scrolled &&
    css`
      box-shadow: 0 ${rem(2)} ${rem(8)} rgba(152, 168, 188, 0.2);
      background-color: ${colors.primarySunYellow100};
    `}

    ${active &&
    css`
      height: 100dvh;
      background-image: linear-gradient(
        ${colors.secondarySoftPurple800},
        ${colors.secondarySoftPurple800}
      );
      overflow-y: auto;
    `}
  `,
);

const StyledHeaderContainer = styled(Container)`
  display: flex;
  width: 100%;
  justify-content: space-between;
  height: auto;
  flex-wrap: wrap;
  align-items: center;
`;

const StyledLogo = styled.div`
  display: flex;
  align-items: center;
  z-index: 10;

  ${mixins.responsiveStyles({
    height: { zero: 40, medium: 53 },
    width: { zero: 40, medium: 53 },
  })}

  a {
    display: flex;
    width: inherit;
    height: inherit;

    svg {
      width: inherit;
      height: inherit;
    }
  }
`;

const Header = ({ langObj, intl, isMainHub = false }: HeaderProps) => {
  const [menuActive, setMenuActive] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const { nodes } = useNavigationQuery();

  const langNode = nodes.filter(
    (node: { node_locale: string }) => node.node_locale == intl.locale,
  )[0];

  const toggleVisibility = () => {
    setMenuActive(!menuActive);

    if (menuActive) {
      document.body.classList.remove('nav--open');
    } else {
      document.body.classList.add('nav--open');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <StyledHeader
      id="main-header"
      active={menuActive}
      scrolled={headerScrolled}
      {...(menuActive && { className: 'hamburger-active' })}
    >
      <StyledHeaderContainer layout>
        {!isMainHub || (isMainHub && (headerScrolled || menuActive)) ? (
          <StyledLogo>
            <LogoHeader
              name={langNode.campaignName}
              slug={createRoutingURI({
                root: '',
                slug: langNode?.mainHub?.slug,
                locale: intl.locale,
              })}
            />
          </StyledLogo>
        ) : null}
        <Navbar
          hamburgerActive={menuActive}
          toggleHamburger={toggleVisibility}
          menuTitle={intl.formatMessage({
            id: 'nav.hamburger.menu.title',
          })}
          searchSubmitText={intl.formatMessage({ id: 'search.submit' })}
        />
        <Navigation isVisible={menuActive} langObj={langObj} node={langNode} />
      </StyledHeaderContainer>
    </StyledHeader>
  );
};

export type HeaderProps = {
  langObj: {
    actual: string;
    switcher: object[];
  };
  intl: IntlShape;
  isMainHub?: boolean;
};

export default injectIntl(Header);
